import { useMemo, useState } from "react";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import {
  buildNoActiveThreadCommandTaskRows,
  type NoActiveThreadCommandTaskRow,
} from "../NoActiveThreadState.logic";
import { mapPrimaryCmmdWorkIntakeToHomebaseContext, type HomebaseCmmdContext } from "./cmmdContext";
import type {
  CmmdWorkIntake,
  CmmdCampaign,
  CmmdProject,
  CmmdTask,
  CmmdWorkspace,
} from "@cmmd-forge/contracts";

function summarizeWorkIntakeError(message: string): string {
  if (message.includes("Authentication required")) {
    return "CMMD authentication is required. Log in again to continue.";
  }
  if (message.includes("non-JSON response")) {
    return "CMMD intake route is unavailable in the current local desktop session.";
  }
  if (message.includes("Failed to load CMMD")) {
    return "Could not sync CMMD data. Reconnect from Settings > Connections and refresh.";
  }
  return message;
}

function resolveContextSourceLabel(context: HomebaseCmmdContext | null): string {
  const source = context?.source;
  if (!source) return "CMMD not connected";
  try {
    return new URL(source).host;
  } catch {
    return source;
  }
}

export interface CmmdWorkIntakeViewModel {
  readonly context: HomebaseCmmdContext | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly compactError: string | null;
  readonly sourceLabel: string;
  readonly taskRows: ReadonlyArray<NoActiveThreadCommandTaskRow>;
  readonly selectedTaskId: string | null;
  readonly selectedTask: NoActiveThreadCommandTaskRow | null;
  readonly setSelectedTaskId: (taskId: string) => void;
}

export function useCmmdWorkIntakeViewModel(): CmmdWorkIntakeViewModel {
  const {
    connected,
    isLoading,
    error,
    source,
    fetchedAt,
    workspace,
    identity,
    campaigns,
    projects,
    tasks,
    workspaces,
  } = useCmmdWorkIntakeSnapshot();

  const intake = useMemo<CmmdWorkIntake>(
    () => ({
      connected,
      source: source ?? "",
      fetchedAt: fetchedAt ?? new Date().toISOString(),
      workspace,
      identity,
      campaigns: campaigns as CmmdCampaign[],
      projects: projects as CmmdProject[],
      tasks: tasks as CmmdTask[],
      workspaces: workspaces as CmmdWorkspace[],
      warnings: [],
    }),
    [connected, source, fetchedAt, workspace, identity, campaigns, projects, tasks, workspaces],
  );

  const context = useMemo<HomebaseCmmdContext | null>(
    () => (connected ? mapPrimaryCmmdWorkIntakeToHomebaseContext(intake) : null),
    [connected, intake],
  );

  const taskRows = useMemo(() => {
    if (!context) return [];
    return buildNoActiveThreadCommandTaskRows({
      campaigns: context.campaigns,
      projects: context.projects,
      tasks: context.tasks,
    });
  }, [context]);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    () => taskRows[0]?.id ?? null,
  );

  const selectedTask = useMemo(
    () => taskRows.find((t) => t.id === selectedTaskId) ?? null,
    [taskRows, selectedTaskId],
  );

  const compactError = error ? summarizeWorkIntakeError(error) : null;
  const sourceLabel = useMemo(() => resolveContextSourceLabel(context), [context]);

  return {
    context,
    isLoading,
    error,
    compactError,
    sourceLabel,
    taskRows,
    selectedTaskId,
    selectedTask,
    setSelectedTaskId,
  };
}
